import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ToastProvider } from './context/ToastContext';
import { OrganizationProvider, useOrganization } from './context/OrganizationContext';

// Layouts
import PublicLayout from './layouts/PublicLayout';
import MemberLayout from './layouts/MemberLayout';
import AdminLayout from './layouts/AdminLayout';

// Public Pages
import Home from './pages/public/Home';
import About from './pages/public/About';
import Blog from './pages/public/Blog';
import BlogDetail from './pages/public/BlogDetail';
import Contact from './pages/public/Contact';
import MailingSignup from './pages/public/MailingSignup';
import MailingUnsubscribe from './pages/admin/MailingUnsubscribe';
import PageView from './pages/public/PageView';
import OrganizationStructure from './pages/public/OrganizationStructure';
import Issues from './pages/public/Issues';
import IssueDetail from './pages/public/IssueDetail';

// Auth Pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';

// Member Pages
import MemberDashboard from './pages/member/Dashboard';
import MemberProfile from './pages/member/Profile';
import MyQRCode from './pages/member/MyQRCode';
import ChangePassword from './pages/member/ChangePassword';

// Admin Pages
import AdminDashboard from './pages/admin/Dashboard';
import CheckInStation from './pages/admin/CheckInStation';
import GiftStation from './pages/admin/GiftStation';
import AdminMembers from './pages/admin/Members';
import MemberCard from './pages/admin/MemberCard';
import MemberCreate from './pages/admin/MemberCreate';
import MemberPrint from './pages/admin/MemberPrint';
import MailingAdmin from './pages/admin/MailingAdmin';
import AdminBlog from './pages/admin/Blog';
import AdminIssues from './pages/admin/Issues';
import AdminEvents from './pages/admin/Events';
import EventForm from './pages/admin/EventForm';
import EventParticipants from './pages/admin/EventParticipants';
import ContentAdmin from './pages/admin/ContentAdmin';
import AdvancedSettings, { StaffAccounts, MemberAdvanced, AuditLogPage } from './pages/admin/AdvancedSettings';
import IssueForm from './pages/admin/IssueForm';
import ContactMessages from './pages/admin/ContactMessages';
import BlogForm from './pages/admin/BlogForm';
import OrganizationMembers from './pages/admin/OrganizationMembers';
import OrganizationMemberForm from './pages/admin/OrganizationMemberForm';
import SiteSettings from './pages/admin/SiteSettings';

// Protected Route Component
import ProtectedRoute from './components/ProtectedRoute';
import ScrollToTop from './components/ScrollToTop';

function AppContent() {
  const { organization } = useOrganization();

  useEffect(() => {
    if (organization) {
      document.title = organization.chinese_name || organization.english_name || organization.name || 'Hong Kong Synergies';
      if (organization.primary_color) {
        document.documentElement.style.setProperty('--primary-color', organization.primary_color);
      }
    }
  }, [organization]);

  return (
    <>
    <ScrollToTop />
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<PublicLayout />}>
        <Route index element={<Home />} />
        <Route path="about" element={<About />} />
        <Route path="blog" element={<Blog />} />
        <Route path="blog/:id" element={<BlogDetail />} />
        <Route path="contact" element={<Contact />} />
        <Route path="mailing" element={<MailingSignup />} />
        <Route path="mailing/unsubscribe/:token" element={<MailingUnsubscribe />} />
        <Route path="organization-structure" element={<OrganizationStructure />} />
        <Route path="services" element={<Issues />} />
        <Route path="services/:slug" element={<IssueDetail />} />
        <Route path="page/:slug" element={<PageView />} />
      </Route>

      {/* Auth Routes */}
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Member Routes */}
      <Route path="/member" element={<ProtectedRoute><MemberLayout /></ProtectedRoute>}>
        <Route index element={<MemberDashboard />} />
        <Route path="profile" element={<MemberProfile />} />
        <Route path="qr-code" element={<MyQRCode />} />
        <Route path="change-password" element={<ChangePassword />} />
      </Route>

      {/* Admin Routes */}
      <Route path="/admin" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
        <Route index element={<AdminDashboard />} />
        <Route path="scan" element={<Navigate to="/admin/members" replace />} />
        <Route path="check-in" element={<CheckInStation />} />
        <Route path="gifts" element={<GiftStation />} />
        <Route path="members" element={<AdminMembers />} />
        <Route path="members/new" element={<MemberCreate />} />
        <Route path="members/:id/print" element={<MemberPrint />} />
        <Route path="members/:id" element={<MemberCard />} />
        <Route path="mailing" element={<MailingAdmin />} />
        <Route path="events" element={<AdminEvents />} />
        <Route path="events/new" element={<EventForm />} />
        <Route path="events/:id/edit" element={<EventForm />} />
        <Route path="events/:id" element={<EventParticipants />} />
        <Route path="content" element={<ContentAdmin />}>
          <Route index element={<Navigate to="settings" replace />} />
          <Route path="settings" element={<SiteSettings />} />
          <Route path="organization" element={<OrganizationMembers />} />
          <Route path="issues" element={<AdminIssues />} />
          <Route path="blog" element={<AdminBlog />} />
        </Route>
        <Route path="blog" element={<Navigate to="/admin/content/blog" replace />} />
        <Route path="blog/new" element={<BlogForm />} />
        <Route path="blog/:id/edit" element={<BlogForm />} />
        <Route path="issues" element={<Navigate to="/admin/content/issues" replace />} />
        <Route path="issues/new" element={<IssueForm />} />
        <Route path="issues/:id/edit" element={<IssueForm />} />
        <Route path="organization-members" element={<Navigate to="/admin/content/organization" replace />} />
        <Route path="organization-members/new" element={<OrganizationMemberForm />} />
        <Route path="organization-members/:id/edit" element={<OrganizationMemberForm />} />
        <Route path="settings" element={<Navigate to="/admin/content/settings" replace />} />
        <Route path="advanced" element={<AdvancedSettings />}>
          <Route index element={<Navigate to="staff" replace />} />
          <Route path="staff" element={<StaffAccounts />} />
          <Route path="members" element={<MemberAdvanced />} />
          <Route path="audit" element={<AuditLogPage />} />
        </Route>
        <Route path="messages" element={<ContactMessages />} />
      </Route>
    </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <ToastProvider>
        <AuthProvider>
          <OrganizationProvider>
            <AppContent />
          </OrganizationProvider>
        </AuthProvider>
      </ToastProvider>
    </Router>
  );
}

export default App;
